import React from "react";
import { useTranslation } from "react-i18next";
import { PiFlipHorizontalFill, PiFlipVerticalFill } from "react-icons/pi";
import { DEFAULT_DOOR_HEIGHT } from "../../../../features/floorplan3d/geometry";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { DoorType, isSlidingDoorType, SlidingDoorType } from "../../../../types/floorplannerTypes";
import { canonicalizeFillPattern } from "../../../../utils/fillPatterns";
import {
  resolveDoorHandleStyle,
  resolveDoorStyle,
} from "../../../../features/floorplan3d/symbols/openings/doors/door3DStyle";
import DimensionField from "../components/DimensionField";
import {
  DoorAppearanceOptionGrid,
  DoorHandleOptionGrid,
} from "../components/DoorStyleOptionGrid";
import LinkedDimensionsGroup from "../components/LinkedDimensionsGroup";
import MetricsSection from "../components/MetricsSection";
import SubsectionTitle from "../components/SubsectionTitle";
import ToggleRow from "../components/ToggleRow";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  areNumbersEqual,
  clampDoorAngleDegrees,
  degreesToRadians,
  getCommonMillimeterValue,
  getCommonValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";

type SingleLeafDoor = DoorType | SlidingDoorType;

type DoorSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedDoors: SingleLeafDoor[];
  displayedDoors: SingleLeafDoor[];
  is3DView: boolean;
};

const getDoorHeight = (door: SingleLeafDoor) =>
  door.threeD?.height ?? DEFAULT_DOOR_HEIGHT;

const isGlassSlidingDoor = (door: SingleLeafDoor) =>
  isSlidingDoorType(door) &&
  (canonicalizeFillPattern(door.fillPattern) === "glass" ||
    door.libraryKey === "MANUAL/GlassSlidingDoor");

const DoorSection: React.FC<DoorSectionProps> = ({
  selectedDoors,
  displayedDoors,
  updateGlobalState,
  is3DView }) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");
  // Use boolean callbacks so `.every`/`.some` don't narrow selectedDoors to never.
  const selectedDoorCount = selectedDoors.length;
  const allSliding = selectedDoors.every((door) => isSlidingDoorType(door));
  const anySliding = selectedDoors.some((door) => isSlidingDoorType(door));
  const allGlassSliding = selectedDoors.every(isGlassSlidingDoor);

  return (
    <>
      {displayedDoors.map((door) => {
        // Doors default unlocked; only lock when explicitly true.
        const aspectLocked = Boolean(door.lockAspectRatio);
        return (
        <div key={`singleDoor-${door?.id}`}>
          <div className="border-b border-gray-300" />
          <div className="text-sm font-normal">
            <div className={panelObjectTitleRowClassName}>
              <UnitHeader
                title={
                  selectedDoorCount > 1
                    ? allSliding
                      ? allGlassSliding
                        ? t("propertyPanel.multipleGlassSlidingDoors", {
                            count: selectedDoorCount })
                        : t("propertyPanel.multipleSlidingDoors", {
                            count: selectedDoorCount })
                      : t("propertyPanel.multipleSingleDoors", {
                          count: selectedDoorCount })
                    : isSlidingDoorType(door)
                      ? isGlassSlidingDoor(door)
                        ? t("propertyPanel.glassSlidingDoorTitle")
                        : t("propertyPanel.slidingDoorTitle")
                      : t("propertyPanel.singleDoorTitle")
                }
              />
            </div>
            <MetricsSection>
              {is3DView ? (
                <LinkedDimensionsGroup
                  locked={aspectLocked}
                  onToggleLock={() => {
                    const newLockState = !aspectLocked;
                    selectedDoors.forEach((selectedDoor) => {
                      floorplannerStore.updateSymbolProperty(
                        selectedDoor.id,
                        "lockAspectRatio",
                        newLockState,
                      );
                      if (newLockState) {
                        const ratio =
                          (selectedDoor.doorWidth || 1) /
                          (getDoorHeight(selectedDoor) || 1);
                        floorplannerStore.updateSymbolProperty(
                          selectedDoor.id,
                          "ratio",
                          ratio,
                        );
                      }
                    });
                  }}
                  widthField={
                    <DimensionField
                      label={t("propertyPanel.width")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedDoors.length > 1
                          ? (getCommonMillimeterValue(
                              selectedDoors,
                              (symbol) => symbol.doorWidth,
                            ) ?? "")
                          : toDisplayLength(door?.doorWidth || 0)
                      }
                      placeholder={
                        selectedDoors.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) => {
                        const value = parseFloat(nextValue);
                        if (!Number.isFinite(value)) return;
                        updateGlobalState(selectedDoors, "doorWidth", value);
                        if (aspectLocked) {
                          const ratio =
                            door.ratio ||
                            (door.doorWidth || 1) / (getDoorHeight(door) || 1);
                          const currentWidth = door.doorWidth || 0;
                          if (currentWidth > 0) {
                            const scale =
                              toWorldFromDisplay(value) / currentWidth;
                            updateGlobalState(
                              selectedDoors,
                              "symbol3DHeight",
                              toDisplayLength(getDoorHeight(door) * scale),
                            );
                          } else {
                            updateGlobalState(
                              selectedDoors,
                              "symbol3DHeight",
                              value / ratio,
                            );
                          }
                        }
                      }}
                    />
                  }
                  lengthField={
                    <DimensionField
                      label={t("propertyPanel.height")}
                      fieldClassName={panelGridFieldClassName}
                      value={
                        selectedDoors.length > 1
                          ? (getCommonMillimeterValue(
                              selectedDoors,
                              getDoorHeight,
                            ) ?? "")
                          : toDisplayLength(getDoorHeight(door))
                      }
                      placeholder={
                        selectedDoors.length > 1 ? mixed : undefined
                      }
                      onChange={(nextValue) => {
                        const value = parseFloat(nextValue);
                        if (!Number.isFinite(value)) return;
                        updateGlobalState(
                          selectedDoors,
                          "symbol3DHeight",
                          value,
                        );
                        if (aspectLocked) {
                          const currentHeight = getDoorHeight(door);
                          if (currentHeight > 0) {
                            const scale =
                              toWorldFromDisplay(value) / currentHeight;
                            updateGlobalState(
                              selectedDoors,
                              "doorWidth",
                              toDisplayLength((door.doorWidth || 0) * scale),
                            );
                          }
                        }
                      }}
                    />
                  }
                />
              ) : (
                <div className="grid items-end gap-2 py-1 grid-cols-1">
                  <DimensionField
                    label={t("propertyPanel.width")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selectedDoors.length > 1
                        ? (getCommonMillimeterValue(
                            selectedDoors,
                            (symbol) => symbol.doorWidth,
                          ) ?? "")
                        : toDisplayLength(door?.doorWidth || 0)
                    }
                    placeholder={
                      selectedDoors.length > 1 ? mixed : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedDoors,
                        "doorWidth",
                        parseFloat(nextValue),
                      )
                    }
                  />
                </div>
              )}
            </MetricsSection>
            {is3DView && (
              <>
                {/*
                  Glass sliding leaf appearance is owned by Fill → Glass /
                  library key; skip the style grid so it does not fight that path.
                */}
                {!allGlassSliding && (
                  <>
                    <SubsectionTitle>
                      {t("propertyPanel.doorAppearance")}
                    </SubsectionTitle>
                    <DoorAppearanceOptionGrid
                      value={
                        selectedDoorCount > 1
                          ? (getCommonValue(selectedDoors, (symbol) =>
                              resolveDoorStyle(symbol.threeD?.doorStyle),
                            ) ?? "")
                          : resolveDoorStyle(door.threeD?.doorStyle)
                      }
                      mixedLabel={mixed}
                      onChange={(style) =>
                        updateGlobalState(selectedDoors, "doorStyle", style)
                      }
                    />
                  </>
                )}
                <SubsectionTitle>
                  {t("propertyPanel.doorHandles")}
                </SubsectionTitle>
                <DoorHandleOptionGrid
                  value={
                    selectedDoorCount > 1
                      ? (getCommonValue(selectedDoors, (symbol) =>
                          resolveDoorHandleStyle(
                            symbol.threeD?.doorHandleStyle,
                            {
                              fallback: isSlidingDoorType(symbol)
                                ? isGlassSlidingDoor(symbol)
                                  ? "bar"
                                  : "knob"
                                : undefined,
                            },
                          ),
                        ) ?? "")
                      : resolveDoorHandleStyle(door.threeD?.doorHandleStyle, {
                          fallback: isSlidingDoorType(door)
                            ? isGlassSlidingDoor(door)
                              ? "bar"
                              : "knob"
                            : undefined,
                        })
                  }
                  mixedLabel={mixed}
                  onChange={(style) =>
                    updateGlobalState(selectedDoors, "doorHandleStyle", style)
                  }
                />
              </>
            )}
            {/* Hinged doors use openAngle as radians; sliding doors use the canvas slide handle. */}
            {!anySliding && (
              <>
                {!is3DView && <div className="border-b border-gray-300" />}
                <div className="p-3 flex items-center justify-between py-2">
                  <DimensionField
                    label={t("propertyPanel.openAngle")}
                    min={0}
                    max={90}
                    step={1}
                    value={
                      selectedDoors.length > 1
                        ? (getCommonValue(
                            selectedDoors,
                            (symbol) =>
                              radiansToRoundedDegrees(symbol.openAngle || 0),
                            areNumbersEqual,
                          ) ?? "")
                        : radiansToRoundedDegrees(door?.openAngle || 0)
                    }
                    placeholder={
                      selectedDoors.length > 1 ? mixed : undefined
                    }
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selectedDoors,
                        "openAngle",
                        degreesToRadians(
                          clampDoorAngleDegrees(parseFloat(nextValue) || 0),
                        ),
                      )
                    }
                  />
                </div>
              </>
            )}
            <div className="border-b border-gray-300" />
            <div className="p-3 pl-4 flex items-center justify-between">
              <div>{t("propertyPanel.doorHanging")}</div>
            </div>
            <div className="p-3 items-left py-2">
              <ToggleRow
                icon={<PiFlipHorizontalFill />}
                label={t("propertyPanel.flipDoorBlade")}
                checked={
                  getCommonValue(
                    selectedDoors,
                    (symbol) => symbol.flipHorizontal || false,
                  ) ?? false
                }
                onChange={(checked) =>
                  updateGlobalState(selectedDoors, "flipHorizontal", checked)
                }
              />
              <ToggleRow
                icon={<PiFlipVerticalFill />}
                label={t("propertyPanel.flopDoorBlade")}
                checked={
                  getCommonValue(
                    selectedDoors,
                    (symbol) => symbol.flipVertical || false,
                  ) ?? false
                }
                onChange={(checked) =>
                  updateGlobalState(selectedDoors, "flipVertical", checked)
                }
              />
            </div>
          </div>
          <div className="border-b border-gray-300" />
        </div>
        );
      })}
    </>
  );
};

export default DoorSection;
